import { Grid, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";
import photo from "../about.jpg";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import gsap from "gsap";

// ─────────────────────────────────────────────────────────────────────────────
// Word-by-word blur reveal — each word fades in sharp from a blurred state
// ─────────────────────────────────────────────────────────────────────────────
const WordReveal = ({ text, inView }) => {
  const words = text.split(' ');
  return (
    <>
      {words.map((word, i) => (
        <motion.span
          key={i}
          style={{ display: 'inline-block', marginRight: '0.28em', lineHeight: 1.85 }}
          initial={{ opacity: 0, y: 12, filter: 'blur(5px)' }}
          animate={inView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {}}
          transition={{ duration: 0.48, delay: i * 0.022, ease: [0.22, 1, 0.36, 1] }}
        >
          {word}
        </motion.span>
      ))}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Stat counter — number animates from 0 to target when in view
// ─────────────────────────────────────────────────────────────────────────────
const StatCounter = ({ value, label, suffix = '', delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.8 });
  const count = useMotionValue(0);
  const displayed = useTransform(count, (v) => `${Math.round(v)}${suffix}`);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(count, value, {
      duration: 1.8,
      delay,
      ease: 'easeOut',
    });
    return () => controls.stop();
  }, [inView, value, delay, count]);

  return (
    <motion.div
      ref={ref}
      style={{ textAlign: 'center' }}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Typography
        fontFamily="Alkatra"
        sx={{ fontSize: { xs: 36, md: 46 } }}
        fontWeight={700}
        color="primary"
        lineHeight={1}
      >
        <motion.span>{displayed}</motion.span>
      </Typography>
      <Typography
        fontFamily="Poppins"
        fontSize={11}
        sx={{ opacity: 0.48, letterSpacing: '0.08em', textTransform: 'uppercase', mt: 0.5 }}
      >
        {label}
      </Typography>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};

const BIO = `As a software developer, my primary responsibility is to design, develop, and maintain software applications. This involves working with a team of programmers, designers, and other professionals to create high-quality software products that meet the needs of clients and end-users. Help companies to build complex web applications and provide assurance to develop the project using ReactJS and modern technologies. Had been working since June 2020 and currently owing a good level of experience in the most known technology ReactJS. Also have good grip with the technologies that are been used with ReactJS such as Hasura, GraphQL, Material-UI and many more.`;

const About = () => {
  const bioRef = useRef(null);
  const bioInView = useInView(bioRef, { once: true, amount: 0.15 });
  const photoRef = useRef(null);

  // ── Photo glitch on hover — rapid jitter simulating digital distortion ────
  const handlePhotoHover = () => {
    const el = photoRef.current;
    if (!el) return;
    const tl = gsap.timeline();
    for (let i = 0; i < 5; i++) {
      tl.to(el, {
        x: (Math.random() - 0.5) * 10,
        skewX: (Math.random() - 0.5) * 3,
        filter: `hue-rotate(${Math.round(Math.random() * 30)}deg) saturate(1.2)`,
        duration: 0.07,
        ease: 'none',
      });
    }
    tl.to(el, {
      x: 0,
      skewX: 0,
      filter: 'hue-rotate(0deg) saturate(1)',
      duration: 0.14,
      ease: 'power2.out',
    });
  };

  return (
    <Grid
      minHeight="100vh"
      justifyContent="center"
      alignItems="center"
      display="flex"
      py={2}
      container
      component="div"
      id="about"
    >
      {/* Section heading */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.5 }}
        style={{ width: '100%' }}
      >
        <Grid
          item
          style={{ borderRadius: '0px 40px 0px 40px' }}
          mt={10}
          alignItems="center"
          justifyContent="center"
          display="flex"
          xs={12}
          mb={5}
          border="solid 1px"
        >
          <Typography fontFamily="Alkatra" fontSize={30} fontWeight={700}>
            About Myself
          </Typography>
        </Grid>
      </motion.div>

      {/* Photo — slides in from left, glitches on hover */}
      <Grid item xs={12} md={6} justifyContent="center" alignItems="center" display="flex" p={4}>
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: '80%' }}
        >
          <img
            ref={photoRef}
            src={photo}
            alt="About Utsav"
            onMouseEnter={handlePhotoHover}
            style={{
              borderRadius: '10px',
              width: '100%',
              height: 'auto',
              display: 'block',
              willChange: 'transform, filter',
              cursor: 'crosshair',
            }}
          />
        </motion.div>
      </Grid>

      {/* Bio + stats ─────────────────────────────────────────────────────── */}
      <Grid
        item xs={12} md={6}
        justifyContent="flex-start"
        alignItems="center"
        display="flex"
        flexDirection="column"
        gap={4}
      >
        {/* Word-by-word blur reveal bio */}
        <div ref={bioRef} style={{ width: '100%' }}>
          <Typography
            component="p"
            fontFamily="Poppins"
            sx={{ fontSize: { xs: 15, md: 18 }, textAlign: 'justify' }}
          >
            <WordReveal text={BIO} inView={bioInView} />
          </Typography>
        </div>

        {/* Animated stats row */}
        <Grid
          container
          justifyContent="space-around"
          sx={{
            mt: 1,
            borderTop: '1px solid rgba(128,128,128,0.15)',
            pt: 3,
            width: '100%',
          }}
        >
          <Grid item>
            <StatCounter value={4} suffix="+" label="Years Experience" delay={0} />
          </Grid>
          <Grid item>
            <StatCounter value={4} suffix="" label="Companies" delay={0.15} />
          </Grid>
          <Grid item>
            <StatCounter value={10} suffix="+" label="Projects Built" delay={0.3} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default About;
